import { useEffect, useState } from "react";
import axios from "axios";
import {
    Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, TextField, Typography,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import config from "../config";
import themeColors from "../theme";

const endpoint = `${config.SERVER_URL}/api/admin/cme-requirement-updates`;

export default function CmeRequirementNotifications() {
    const { token } = useAuth();
    const [targets, setTargets] = useState([]);
    const [selected, setSelected] = useState("");
    const [description, setDescription] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [preview, setPreview] = useState(false);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const target = targets.find((item) => `${item.slug}:${item.designation}` === selected);

    useEffect(() => {
        let active = true;
        axios.get(`${endpoint}/targets`, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => { if (active) setTargets(data.targets); })
            .catch(() => { if (active) setError("Could not load state requirements. Reload the page to try again."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [token]);

    async function sendNotification() {
        if (!target || !description.trim() || !confirmed || sending) return;
        setSending(true);
        setError("");
        setResult(null);
        try {
            const { data } = await axios.post(`${endpoint}/notify`, {
                slug: target.slug, designation: target.designation, version: target.version,
                description: description.trim(), requirementsUpdated: confirmed,
            }, { headers: { Authorization: `Bearer ${token}` } });
            setResult(data);
            setPreview(false);
            setConfirmed(false);
        } catch (err) {
            setError(err.response?.data?.error || "Could not complete the notification request. You can retry; already processed recipients will be skipped.");
        } finally {
            setSending(false);
        }
    }

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" color={themeColors.teal} gutterBottom>CME requirement updates</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                After updating the Credit Tracker with new state requirements, enter a brief description below.
                This email goes to verified users who track the selected state and have the selected license type.
                Credit Tracker opt-outs and previously notified recipients are skipped.
            </Typography>
            {loading ? <CircularProgress size={24} aria-label="Loading state requirements" /> : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField select label="State and license type" value={selected} disabled={sending}
                        onChange={(event) => { setSelected(event.target.value); setConfirmed(false); setResult(null); }}>
                        {targets.map((item) => (
                            <MenuItem key={`${item.slug}:${item.designation}`} value={`${item.slug}:${item.designation}`}>
                                {item.state} ({item.designation})
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField label="What’s new" multiline minRows={3} value={description} disabled={sending}
                        inputProps={{ maxLength: 2000 }}
                        helperText="Describe the new requirements. Each line becomes a bullet in the email."
                        onChange={(event) => { setDescription(event.target.value); setConfirmed(false); setResult(null); }} />
                    <FormControlLabel control={<Checkbox checked={confirmed} disabled={sending}
                        onChange={(event) => setConfirmed(event.target.checked)} />}
                        label="I have updated the Credit Tracker with these new requirements." />
                    <Button variant="contained" sx={{ alignSelf: "flex-start", backgroundColor: themeColors.teal }}
                        disabled={!target || !description.trim() || !confirmed || sending}
                        onClick={() => { setError(""); setPreview(true); }}>
                        Preview notification
                    </Button>
                </Box>
            )}
            {error && !preview && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {result && <Alert severity={result.failed ? "warning" : "success"} sx={{ mt: 2 }}>
                {result.sent} sent. {result.alreadyProcessed} already processed. {result.skipped} skipped. {result.failed} failed.
                {result.failed > 0 && " Review delivery logs before retrying uncertain deliveries."}
            </Alert>}
            <Dialog open={preview} onClose={() => { if (!sending) setPreview(false); }} fullWidth maxWidth="sm">
                <DialogTitle>Preview requirement update</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Recipients: users tracking {target?.state} with a {target?.designation} license.
                        Each recipient’s first name will be filled in automatically.
                    </Typography>
                    <Typography fontWeight="bold" mb={2}>Subject: New CME requirements for your {target?.state} license</Typography>
                    <Typography paragraph>Hi [First Name],</Typography>
                    <Typography paragraph><strong>There are new CME requirements for your {target?.state} medical license.</strong></Typography>
                    <Typography paragraph>
                        We’ve updated the <strong>CME Credit Tracker</strong> with the latest requirements so you can easily see what has changed and what you need to complete for your {target?.state} license.
                    </Typography>
                    <Typography fontWeight="bold">What’s new:</Typography>
                    <ul>{description.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line, index) => <li key={index}>{line}</li>)}</ul>
                    <Typography color={themeColors.teal} fontWeight="bold" mb={2}>View My {target?.state} CME Requirements →</Typography>
                    <Typography paragraph>We’ll continue to monitor changes to CME requirements and keep your Credit Tracker up to date.</Typography>
                    <Typography paragraph>— The OnCallCME Team</Typography>
                    <Typography variant="body2" fontStyle="italic">
                        OnCallCME is designed to help you track your CME requirements. Always verify current requirements with your state licensing board.
                    </Typography>
                    <Typography variant="caption" display="block" mt={2}>An unsubscribe and email preferences link is included.</Typography>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button disabled={sending} onClick={() => setPreview(false)}>Back to editing</Button>
                    <Button variant="contained" disabled={sending} onClick={sendNotification}>
                        {sending ? "Sending…" : "Send requirement update"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
